import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentProps,
  type CSSProperties,
  type FormEvent,
  type HTMLAttributes,
  type PointerEvent as ReactPointerEvent
} from 'react';
import {
  closestCenter,
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import type { CreateTaskRequest, SectionDto, TaskDto, TaskListParams } from '../api/types';
import { IconChevron } from '../components/icons';
import { TaskRow } from '../components/TaskRow';
import {
  useCompletedTasks,
  useCreateTask,
  useProjects,
  useReorderTasks,
  useSortTodayByIce,
  useTags,
  useViewTasks
} from '../hooks/queries';
import { todayIso } from '../utils/dates';
import { useWorkspace } from '../workspace/WorkspaceContext';

export type ListKind = 'today' | 'incoming' | 'inbox' | 'completed' | 'project' | 'tag';

interface TaskListPageProps {
  kind: ListKind;
}

const EMPTY_MARKS: Record<ListKind, string> = {
  today: '✶',
  incoming: '◠',
  inbox: '▭',
  completed: '✓',
  project: '❡',
  tag: '#'
};

/** Views whose order is backed by task position and can be rearranged by hand. */
const REORDERABLE_KINDS: ReadonlySet<ListKind> = new Set(['today', 'inbox', 'project']);

/**
 * PointerSensor that ignores touch input, so the mouse gets a small
 * drag-distance threshold while TouchSensor (long-press) owns touch devices
 * and normal page scrolling keeps working there.
 */
class MousePointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: 'onPointerDown' as const,
      handler: ({ nativeEvent }: ReactPointerEvent): boolean =>
        nativeEvent.isPrimary && nativeEvent.button === 0 && nativeEvent.pointerType !== 'touch'
    }
  ];
}

function SortableTaskRow(props: ComponentProps<typeof TaskRow>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: props.task.id
  });

  const style: CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(
      transform && isDragging ? { ...transform, scaleX: 1.02, scaleY: 1.02 } : transform
    ),
    transition
  };

  return (
    <TaskRow
      {...props}
      style={style}
      innerRef={setNodeRef}
      dragProps={{ ...attributes, ...listeners } as HTMLAttributes<HTMLDivElement>}
      dragging={isDragging}
    />
  );
}

export function TaskListPage({ kind }: TaskListPageProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const { id } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTaskId = searchParams.get('task');

  const projects = useProjects(workspace.id);
  const tags = useTags(workspace.id);

  const isCompletedView = kind === 'completed';

  const listParams = useMemo<TaskListParams>(() => {
    if (kind === 'project') return { projectId: id };
    if (kind === 'tag') return { tagId: id };
    if (kind === 'completed') return { view: 'inbox' };
    return { view: kind };
  }, [kind, id]);

  const activeQuery = useViewTasks(workspace.id, listParams, !isCompletedView);
  const completedQuery = useCompletedTasks(workspace.id);
  // A project's finished work, revealed on demand: it is history, not the
  // working list, so it stays folded away until asked for.
  const [showDone, setShowDone] = useState(false);
  const projectDone = useCompletedTasks(workspace.id, kind === 'project' ? id : undefined,
    kind === 'project' && showDone);
  const doneTasks = useMemo(
    () => projectDone.data?.pages.flatMap((p) => p.items) ?? [],
    [projectDone.data]
  );
  const doneSentinel = useRef<HTMLDivElement | null>(null);

  // Load the next page as the end of the list comes into view, rather than
  // making the reader hunt for a button.
  useEffect(() => {
    const node = doneSentinel.current;
    if (!node || !showDone || !projectDone.hasNextPage) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !projectDone.isFetchingNextPage) {
          projectDone.fetchNextPage();
        }
      },
      { rootMargin: '120px' }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [showDone, projectDone.hasNextPage, projectDone.isFetchingNextPage, doneTasks.length]);

  const tasks: TaskDto[] = useMemo(() => {
    if (isCompletedView) {
      return completedQuery.data?.pages.flatMap((page) => page.items) ?? [];
    }
    return activeQuery.data ?? [];
  }, [isCompletedView, completedQuery.data, activeQuery.data]);

  const projectsById = useMemo(
    () => new Map((projects.data ?? []).map((p) => [p.id, p])),
    [projects.data]
  );
  const tagsById = useMemo(() => new Map((tags.data ?? []).map((tg) => [tg.id, tg])), [tags.data]);

  useEffect(() => {
    setShowDone(false);
  }, [kind, id]);

  const currentProject = kind === 'project' && id ? projectsById.get(id) : undefined;
  const currentTag = kind === 'tag' && id ? tagsById.get(id) : undefined;

  const title =
    kind === 'project'
      ? currentProject?.name ?? ''
      : kind === 'tag'
        ? currentTag?.name ?? ''
        : t(`views.${kind}`);

  const openTask = (taskId: string) => {
    if (suppressClickRef.current) return;
    const next = new URLSearchParams(searchParams);
    next.set('task', taskId);
    setSearchParams(next);
  };

  const emptyKey = kind === 'project' ? 'empty.project' : kind === 'tag' ? 'empty.tag' : `empty.${kind}`;

  const grouped = useMemo(() => {
    if (kind !== 'project' || !currentProject) return null;
    const sections = [...currentProject.sections].sort((a, b) => a.position - b.position);
    const bySection = new Map<string, TaskDto[]>();
    const loose: TaskDto[] = [];
    for (const task of tasks) {
      const section = task.sectionId && sections.some((s) => s.id === task.sectionId)
        ? task.sectionId
        : null;
      if (section === null) {
        loose.push(task);
      } else {
        const list = bySection.get(section) ?? [];
        list.push(task);
        bySection.set(section, list);
      }
    }
    return { sections, bySection, loose };
  }, [kind, currentProject, tasks]);

  const canReorder = REORDERABLE_KINDS.has(kind);
  const reorder = useReorderTasks(workspace.id, listParams);
  const sortByIce = useSortTodayByIce(workspace.id);
  const hasIceScores = kind === 'today' && tasks.some((task) => task.iceScore !== null);
  const [dragActive, setDragActive] = useState(false);
  const suppressClickRef = useRef(false);

  const sensors = useSensors(
    useSensor(MousePointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 8 } })
  );

  const endDrag = () => {
    setDragActive(false);
    // A click fires on the row right after the drag's pointerup; swallow it
    // so dropping a task does not open the task detail panel.
    suppressClickRef.current = true;
    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    endDrag();
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    // In a grouped project view only the ungrouped (no-section) list is sortable.
    const list = grouped ? grouped.loose : tasks;
    const oldIndex = list.findIndex((task) => task.id === active.id);
    const newIndex = list.findIndex((task) => task.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(list, oldIndex, newIndex);
    let next = reordered;
    if (grouped) {
      const looseIds = new Set(list.map((task) => task.id));
      next = [...reordered, ...tasks.filter((task) => !looseIds.has(task.id))];
    }
    reorder.mutate(next);
  };

  const renderRow = (task: TaskDto, index: number, sortable = false) => {
    const Row = sortable ? SortableTaskRow : TaskRow;
    return (
      <Row
        key={task.id}
        task={task}
        projectsById={projectsById}
        tagsById={tagsById}
        muted={isCompletedView}
        selected={task.id === selectedTaskId}
        showProject={kind !== 'project'}
        showIce={kind === 'today'}
        onOpen={openTask}
        style={{ animationDelay: `${Math.min(index, 14) * 22}ms` }}
      />
    );
  };

  const renderSortableList = (list: TaskDto[]) => (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={() => setDragActive(true)}
      onDragCancel={endDrag}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={list.map((task) => task.id)} strategy={verticalListSortingStrategy}>
        {list.map((task, index) => renderRow(task, index, true))}
      </SortableContext>
    </DndContext>
  );

  const isLoading = isCompletedView ? completedQuery.isPending : activeQuery.isPending;

  return (
    <section className="tl">
      <header className="tl-header">
        <h2 className="tl-title">
          {currentProject && (
            <span className="color-dot" style={{ background: currentProject.color }} />
          )}
          {currentTag && <span className="color-dot" style={{ background: currentTag.color }} />}
          {title}
        </h2>
        {!isLoading && (
          <span className="tl-count">{t('tasks.count', { count: tasks.length })}</span>
        )}
        {hasIceScores && (
          <button
            type="button"
            className="tl-ice-sort"
            onClick={() => sortByIce.mutate()}
            disabled={sortByIce.isPending}
            title={t('ice.sortHint')}
          >
            {t('ice.sort')}
          </button>
        )}
      </header>

      <QuickAdd kind={kind} contextId={id} />

      <div className={`tl-scroll${dragActive ? ' is-dragging' : ''}`}>
        {isLoading && <div className="tl-loading">{t('app.loading')}</div>}

        {!isLoading && tasks.length === 0 && (
          <div className="tl-empty">
            <div className="tl-empty-mark">{EMPTY_MARKS[kind]}</div>
            <p>{t(emptyKey)}</p>
          </div>
        )}

        {!isLoading && grouped && (
          <>
            {canReorder
              ? renderSortableList(grouped.loose)
              : grouped.loose.map((task, index) => renderRow(task, index))}
            {grouped.sections.map((section: SectionDto) => {
              const sectionTasks = grouped.bySection.get(section.id) ?? [];
              if (sectionTasks.length === 0) return null;
              return (
                <div key={section.id}>
                  <div className="tl-section-head">{section.name}</div>
                  {sectionTasks.map((task, index) => renderRow(task, index))}
                </div>
              );
            })}
          </>
        )}

        {!isLoading &&
          !grouped &&
          (canReorder
            ? renderSortableList(tasks)
            : tasks.map((task, index) => renderRow(task, index)))}

        {kind === 'project' && !isLoading && (
          <div className="tl-done">
            <button
              type="button"
              className="tl-done-toggle"
              onClick={() => setShowDone((v) => !v)}
              aria-expanded={showDone}
            >
              <IconChevron className={`tl-done-caret${showDone ? ' open' : ''}`} />
              <span>{showDone ? t('tasks.hideCompleted') : t('tasks.showCompleted')}</span>
            </button>

            {showDone && (
              <>
                {projectDone.isPending && <div className="tl-loading">{t('app.loading')}</div>}
                {!projectDone.isPending && doneTasks.length === 0 && (
                  <p className="tl-done-empty">{t('tasks.noCompleted')}</p>
                )}
                {doneTasks.map((task, index) => (
                  <TaskRow
                    key={task.id}
                    task={task}
                    projectsById={projectsById}
                    tagsById={tagsById}
                    muted
                    selected={task.id === selectedTaskId}
                    showProject={false}
                    onOpen={openTask}
                    style={{ animationDelay: `${Math.min(index, 14) * 22}ms` }}
                  />
                ))}
                <div ref={doneSentinel} />
                {projectDone.isFetchingNextPage && (
                  <div className="tl-loading">{t('tasks.loadingMore')}</div>
                )}
              </>
            )}
          </div>
        )}

        {isCompletedView && completedQuery.hasNextPage && (
          <button
            type="button"
            className="btn tl-load-more"
            onClick={() => completedQuery.fetchNextPage()}
            disabled={completedQuery.isFetchingNextPage}
          >
            {completedQuery.isFetchingNextPage ? t('tasks.loadingMore') : t('tasks.loadMore')}
          </button>
        )}
      </div>
    </section>
  );
}

interface QuickAddProps {
  kind: ListKind;
  contextId?: string;
}

function QuickAdd({ kind, contextId }: QuickAddProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const createTask = useCreateTask(workspace.id);
  const [title, setTitle] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = title.trim();
    if (!trimmed || createTask.isPending) return;
    const body: CreateTaskRequest = { title: trimmed };
    if (kind === 'today') body.starred = true;
    if (kind === 'incoming') body.dueDate = todayIso();
    if (kind === 'project' && contextId) body.projectId = contextId;
    if (kind === 'tag' && contextId) body.tagIds = [contextId];
    createTask.mutate(body, { onSuccess: () => setTitle('') });
  };

  return (
    <form className="quick-add" onSubmit={submit}>
      <span className="quick-add-plus" aria-hidden="true">
        +
      </span>
      <input
        value={title}
        placeholder={t('tasks.quickAdd')}
        onChange={(e) => setTitle(e.target.value)}
      />
    </form>
  );
}
